import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { WithdrawMode } from '../interfaces/Withdrawal';

/**
 * Client-side state for the withdrawal flow.
 *
 * `/withdraw`, `/withdraw/add-mode` and `/withdraw/status` are separate routes
 * that all need the same session, so this cannot live in component state.
 * Server data stays in react-query — only the session handle and the user's
 * selections live here.
 *
 * Persisted to sessionStorage so a page refresh on the status screen can still
 * recover its `sessionId`. It is deliberately not in localStorage: a withdrawal
 * session is short-lived and bounded by `cacheTTL`.
 */

export const WITHDRAW_SESSION_KEY = 'withdraw-session';

export interface IWithdrawState {
  sessionId?: string;
  /** When the session was issued, for the `cacheTTL` check. */
  sessionTimestamp?: number;
  /** Amount in whole rupees. Never a float — see `amountInRupees`. */
  amount?: number;
  selectedMode?: WithdrawMode;
  /**
   * Set after a UPI/bank is linked, so the withdrawal screen knows to re-open
   * its session. A plain flag rather than the returned `payoutDetails`, because
   * the server may send an empty object back and an empty payload must still
   * trigger the refresh.
   */
  modeJustAdded?: boolean;
  /** Id of a nudge shown by the withdrawal gate, echoed back on submit. */
  shownNudgeId?: string;
  resetWithdrawStore: () => void;
}

const initialWithdrawState = {
  sessionId: undefined,
  sessionTimestamp: undefined,
  amount: undefined,
  selectedMode: undefined,
  modeJustAdded: false,
  shownNudgeId: undefined,
};

export const useWithdrawStore = create<IWithdrawState>()(
  persist(
    (set) => ({
      ...initialWithdrawState,
      resetWithdrawStore: () => set({ ...initialWithdrawState }),
    }),
    {
      name: WITHDRAW_SESSION_KEY,
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        sessionId: state.sessionId,
        sessionTimestamp: state.sessionTimestamp,
        amount: state.amount,
        selectedMode: state.selectedMode,
        shownNudgeId: state.shownNudgeId,
      }),
    },
  ),
);

export function setWithdrawSession(sessionId?: string) {
  useWithdrawStore.setState({ sessionId, sessionTimestamp: Date.now() });
}

export function setWithdrawAmount(amount?: number) {
  useWithdrawStore.setState({ amount });
}

export function setSelectedWithdrawMode(selectedMode?: WithdrawMode) {
  useWithdrawStore.setState({ selectedMode });
}

export function setModeJustAdded(modeJustAdded: boolean) {
  useWithdrawStore.setState({ modeJustAdded });
}

export function setShownNudgeId(shownNudgeId?: string) {
  useWithdrawStore.setState({ shownNudgeId });
}

export function resetWithdrawStore() {
  useWithdrawStore.getState().resetWithdrawStore();
  sessionStorage.removeItem(WITHDRAW_SESSION_KEY);
}
